import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Eye,
  EyeOff,
  Check,
  ChevronLeft,
  ChevronRight,
  Loader2,
  AlertCircle,
  QrCode,
} from "lucide-react";

// ────────────────────────────────────────────────────────────────────────────
// Provider catalog
// ────────────────────────────────────────────────────────────────────────────
const PROVIDERS = [
  {
    key: "gupshup",
    label: "Gupshup",
    description: "BSP oficial de Meta — templates HSM",
    accent: "border-emerald-500 bg-emerald-50 text-emerald-700",
    fields: [
      { key: "api_key", label: "API Key", required: true, secret: true },
      { key: "app_id", label: "App ID", required: true },
      { key: "app_name", label: "App Name", required: true },
      { key: "source_phone", label: "Source Phone", required: true, placeholder: "5215512345678" },
    ],
  },
  {
    key: "twilio",
    label: "Twilio",
    description: "Sandbox o sender aprobado",
    accent: "border-blue-500 bg-blue-50 text-blue-700",
    fields: [
      { key: "account_sid", label: "Account SID", required: true },
      { key: "auth_token", label: "Auth Token", required: true, secret: true },
      { key: "phone", label: "Teléfono Twilio", required: true, placeholder: "+5215512345678" },
    ],
  },
  {
    key: "evolution",
    label: "Evolution",
    description: "Auto-host vía QR",
    accent: "border-orange-500 bg-orange-50 text-orange-700",
    fields: [],
  },
  {
    key: "meta_cloud",
    label: "Meta Cloud",
    description: "WhatsApp Cloud API directa",
    accent: "border-purple-500 bg-purple-50 text-purple-700",
    fields: [
      { key: "phone_number_id", label: "Phone Number ID", required: true },
      { key: "access_token", label: "Access Token", required: true, secret: true },
      { key: "waba_id", label: "WABA ID", required: true },
      { key: "app_id", label: "App ID", required: true },
      { key: "app_secret", label: "App Secret", required: true, secret: true },
      { key: "verify_token", label: "Verify Token", required: true },
    ],
  },
];

const MODULES = [
  { key: "notificaciones", label: "Notificaciones", description: "Avisos a leads y vendedores" },
  { key: "logicas", label: "Lógicas", description: "Mensajería de seguimiento automatizado" },
  { key: "campanas", label: "Campañas", description: "Envíos masivos" },
  { key: "agente_ia", label: "Agente IA", description: "Conversaciones automatizadas" },
];

const STEPS = [
  { id: 1, title: "Proveedor" },
  { id: 2, title: "Scope" },
  { id: 3, title: "Módulos" },
  { id: 4, title: "Resumen" },
];


// ────────────────────────────────────────────────────────────────────────────
// Sub-components
// ────────────────────────────────────────────────────────────────────────────
function StepIndicator({ current }) {
  return (
    <div className="flex items-center justify-between gap-2 px-1 pb-4" data-testid="waba-wizard-stepper">
      {STEPS.map((s, idx) => {
        const active = current === s.id;
        const done = current > s.id;
        return (
          <React.Fragment key={s.id}>
            <div className="flex items-center gap-2">
              <div
                className={[
                  "flex h-7 w-7 items-center justify-center rounded-full text-xs font-semibold transition",
                  done && "bg-indigo-600 text-white",
                  active && "bg-indigo-600 text-white ring-2 ring-indigo-400/40",
                  !active && !done && "bg-gray-700 text-gray-400",
                ]
                  .filter(Boolean)
                  .join(" ")}
              >
                {done ? <Check className="h-3.5 w-3.5" /> : s.id}
              </div>
              <span className={`text-xs font-medium ${active ? "text-white" : "text-gray-400"}`}>
                {s.title}
              </span>
            </div>
            {idx < STEPS.length - 1 && (
              <div className={`h-px flex-1 ${current > s.id ? "bg-indigo-500" : "bg-gray-700"}`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}


function SecretInput({ id, value, onChange, placeholder }) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <Input
        id={id}
        type={show ? "text" : "password"}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="pr-10 mt-1 bg-gray-800 border-gray-600 text-white placeholder-gray-500 focus:border-indigo-500"
        data-testid={`waba-input-${id}`}
      />
      <button
        type="button"
        onClick={() => setShow((s) => !s)}
        className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-gray-400 hover:text-gray-200"
        tabIndex={-1}
        data-testid={`waba-toggle-secret-${id}`}
      >
        {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
      </button>
    </div>
  );
}


function ProviderCard({ provider, selected, onSelect }) {
  return (
    <button
      type="button"
      onClick={() => onSelect(provider.key)}
      className={[
        "flex flex-col items-start gap-1 rounded-lg border p-4 text-left transition",
        selected
          ? "bg-indigo-900/40 border-indigo-500 text-white"
          : "bg-gray-800 border-gray-700 text-gray-200 hover:border-gray-500",
      ].join(" ")}
      data-testid={`waba-provider-card-${provider.key}`}
    >
      <span className="text-sm font-semibold">{provider.label}</span>
      <span className={`text-xs ${selected ? "text-indigo-200" : "text-gray-400"}`}>
        {provider.description}
      </span>
    </button>
  );
}


// ────────────────────────────────────────────────────────────────────────────
// Steps
// ────────────────────────────────────────────────────────────────────────────
function Step1ProviderCreds({ form, setForm }) {
  const provider = PROVIDERS.find((p) => p.key === form.provider);

  return (
    <div className="space-y-5">
      <div>
        <Label htmlFor="waba-name" className="text-sm text-gray-300">Nombre de la cuenta</Label>
        <Input
          id="waba-name"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          placeholder="Ej: WhatsApp ventas Atlas"
          className="mt-1 bg-gray-800 border-gray-600 text-white placeholder-gray-500 focus:border-indigo-500"
          data-testid="waba-input-name"
        />
      </div>

      <div>
        <Label className="text-sm text-gray-300">Proveedor</Label>
        <div className="mt-2 grid grid-cols-2 gap-3">
          {PROVIDERS.map((p) => (
            <ProviderCard
              key={p.key}
              provider={p}
              selected={form.provider === p.key}
              onSelect={(k) =>
                setForm({ ...form, provider: k, credentials: {} })
              }
            />
          ))}
        </div>
      </div>

      {provider && provider.key === "evolution" && (
        <div className="space-y-2 rounded-lg border border-gray-700 bg-gray-800/50 p-4" data-testid="waba-evolution-info">
          <div className="flex items-start gap-3">
            <QrCode className="mt-0.5 h-5 w-5 flex-shrink-0 text-orange-400" />
            <div>
              <p className="text-sm font-medium text-white">
                Evolution API usa conexión via código QR.
              </p>
              <p className="mt-1 text-xs text-gray-400">
                Después de crear la cuenta podrás escanear el QR desde el detalle de la cuenta.
              </p>
            </div>
          </div>
        </div>
      )}

      {provider && provider.fields.length > 0 && (
        <div className="space-y-3 rounded-lg border border-gray-700 bg-gray-800/50 p-4">
          <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">
            Credenciales — {provider.label}
          </p>
          {provider.fields.map((f) => (
            <div key={f.key}>
              <Label htmlFor={`waba-cred-${f.key}`} className="text-xs text-gray-400">
                {f.label} {f.required && <span className="text-red-400">*</span>}
              </Label>
              {f.secret ? (
                <SecretInput
                  id={`waba-cred-${f.key}`}
                  value={form.credentials[f.key] || ""}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      credentials: { ...form.credentials, [f.key]: e.target.value },
                    })
                  }
                  placeholder={f.placeholder}
                />
              ) : (
                <Input
                  id={`waba-cred-${f.key}`}
                  value={form.credentials[f.key] || ""}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      credentials: { ...form.credentials, [f.key]: e.target.value },
                    })
                  }
                  placeholder={f.placeholder}
                  className="mt-1 bg-gray-800 border-gray-600 text-white placeholder-gray-500 focus:border-indigo-500"
                  data-testid={`waba-input-${f.key}`}
                />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


function Step2Scope({ form, setForm, setFallback, devs, products }) {
  const showDevs = devs.status === "ok" && devs.items.length > 0;
  const showProducts = products.status === "ok" && products.items.length > 0;

  const toggleRefId = (id) => {
    const set = new Set(form.scopeRefIds);
    if (set.has(id)) set.delete(id);
    else set.add(id);
    setForm({ ...form, scopeRefIds: Array.from(set) });
  };

  const selectScopeType = (type) => {
    // Reset selecciones al cambiar de tipo
    setForm({ ...form, scopeType: type, scopeRefIds: [] });
  };

  const renderEntityList = (state, kindLabel) => {
    if (state.status === "loading") {
      return <p className="text-xs text-gray-400">Cargando {kindLabel}…</p>;
    }
    if (state.status === "error") {
      return <p className="text-xs text-red-300">Error al cargar {kindLabel}</p>;
    }
    if (!state.items.length) {
      return <p className="text-xs text-gray-400">No hay {kindLabel} creados</p>;
    }
    return (
      <div className="mt-2 max-h-48 space-y-2 overflow-y-auto rounded-md border border-gray-700 bg-gray-900/40 p-3">
        {state.items.map((it) => {
          const checked = form.scopeRefIds.includes(it.id);
          return (
            <label
              key={it.id}
              className="flex cursor-pointer items-center gap-2 text-sm text-gray-200 hover:text-white"
              data-testid={`waba-scope-entity-${it.id}`}
            >
              <Checkbox
                checked={checked}
                onCheckedChange={() => toggleRefId(it.id)}
                className="border-gray-500"
              />
              <span>{it.name || it.id}</span>
            </label>
          );
        })}
      </div>
    );
  };

  const renderOption = ({ key, label, description, selectable, body }) => {
    const selected = form.scopeType === key;
    const disabled = !selectable;
    return (
      <div
        className={[
          "rounded-lg border p-4 transition",
          disabled
            ? "border-gray-800 bg-gray-900/40 opacity-60"
            : selected
            ? "border-indigo-500 bg-indigo-900/40"
            : "border-gray-700 bg-gray-800 hover:border-gray-500 cursor-pointer",
        ].join(" ")}
      >
        <button
          type="button"
          disabled={disabled}
          onClick={() => !disabled && selectScopeType(key)}
          className="flex w-full items-center justify-between text-left"
          data-testid={`waba-scope-option-${key}`}
        >
          <div>
            <span className={`text-sm font-semibold ${selected ? "text-white" : "text-gray-200"}`}>
              {label}
            </span>
            <p className={`mt-1 text-xs ${selected ? "text-indigo-200" : "text-gray-400"}`}>
              {description}
            </p>
          </div>
          {selected && <Check className="h-4 w-4 text-indigo-300" />}
        </button>
        {selected && body}
      </div>
    );
  };

  return (
    <div className="space-y-3">
      <p className="text-sm text-gray-400">Define cuándo se utilizará esta cuenta WABA.</p>

      {/* Global — siempre visible */}
      {renderOption({
        key: "global",
        label: "Global (fallback)",
        description: "Se usará cuando no haya una cuenta asignada al scope específico.",
        selectable: true,
      })}

      {/* Desarrollos — sólo developer/franchise */}
      {showDevs &&
        renderOption({
          key: "development",
          label: "Desarrollos",
          description:
            devs.status === "ok" && !devs.items.length
              ? "No hay desarrollos creados"
              : devs.status === "error"
              ? "Error al cargar"
              : "Asignar a uno o varios desarrollos.",
          selectable: devs.status === "ok" && devs.items.length > 0,
          body: renderEntityList(devs, "desarrollos"),
        })}

      {/* Productos — sólo car_dealer */}
      {showProducts &&
        renderOption({
          key: "product",
          label: "Productos",
          description:
            products.status === "ok" && !products.items.length
              ? "No hay productos creados"
              : products.status === "error"
              ? "Error al cargar"
              : "Asignar a uno o varios productos.",
          selectable: products.status === "ok" && products.items.length > 0,
          body: renderEntityList(products, "productos"),
        })}

      {/* R2-A — Fallback INDEPENDIENTE del scope. Antes lo decidía el scope
          (global→ON, resto→OFF), dejando líneas de producto/desarrollo sin respaldo. */}
      <div className="mt-4 flex items-center justify-between rounded-lg border border-gray-700 bg-gray-800 p-4">
        <div className="pr-3">
          <p className="text-sm font-semibold text-gray-200">Usar como línea de respaldo (fallback)</p>
          <p className="text-xs text-gray-400">
            Se usará cuando no haya una cuenta asignada al scope solicitado (avisos a
            vendedores, seguimientos, campañas). Independiente del scope elegido.
          </p>
        </div>
        <Switch
          checked={!!form.is_global_fallback}
          onCheckedChange={(v) => setFallback(v)}
          data-testid="waba-scope-fallback"
        />
      </div>
    </div>
  );
}


function Step3Modules({ form, setForm }) {
  const activeList = MODULES.filter((m) => form.modules[m.key]).map((m) => m.label);

  return (
    <div className="space-y-3">
      <p className="text-sm text-gray-400">
        Selecciona en qué flujos se usará esta cuenta.
      </p>
      {MODULES.map((m) => {
        const selected = !!form.modules[m.key];
        return (
          <div
            key={m.key}
            className={[
              "flex items-center justify-between rounded-lg border p-4 transition",
              selected
                ? "bg-indigo-900/40 border-indigo-500"
                : "bg-gray-800 border-gray-700",
            ].join(" ")}
            data-testid={`waba-module-card-${m.key}`}
          >
            <div>
              <p className={`text-sm font-semibold ${selected ? "text-white" : "text-gray-200"}`}>
                {m.label}
              </p>
              <p className={`text-xs ${selected ? "text-indigo-200" : "text-gray-400"}`}>
                {m.description}
              </p>
            </div>
            <Switch
              checked={selected}
              onCheckedChange={(v) =>
                setForm({ ...form, modules: { ...form.modules, [m.key]: v } })
              }
              data-testid={`waba-module-toggle-${m.key}`}
            />
          </div>
        );
      })}

      <div className="rounded-lg border border-indigo-500/40 bg-indigo-900/30 p-3 text-xs text-indigo-200">
        <strong className="text-indigo-100">Esta cuenta se usará para:</strong>{" "}
        {activeList.length ? activeList.join(", ") : "—"}
      </div>
    </div>
  );
}


function Step4Summary({ form, devs, products }) {
  const provider = PROVIDERS.find((p) => p.key === form.provider);
  const activeModules = MODULES.filter((m) => form.modules[m.key]).map((m) => m.label);

  let scopeValue;
  if (form.scopeType === "global") {
    scopeValue = "Global (fallback)";
  } else {
    const lookup = form.scopeType === "development" ? devs : products;
    const names = form.scopeRefIds
      .map((id) => (lookup.find((it) => it.id === id) || {}).name || id)
      .join(", ");
    const label = form.scopeType === "development" ? "Desarrollo" : "Producto";
    scopeValue = `${label}${form.scopeRefIds.length > 1 ? "s" : ""}: ${names}`;
  }

  const totalAccounts = form.scopeType === "global" ? 1 : form.scopeRefIds.length;

  return (
    <div className="space-y-3">
      <SummaryRow label="Nombre" value={form.name} />
      <SummaryRow label="Proveedor" value={provider?.label || "—"} />
      <SummaryRow label="Scope" value={scopeValue} />
      <SummaryRow
        label="Módulos"
        value={
          activeModules.length ? (
            <div className="flex flex-wrap gap-1 justify-end">
              {activeModules.map((m) => (
                <Badge key={m} variant="outline" className="text-[10px]">
                  {m}
                </Badge>
              ))}
            </div>
          ) : (
            "—"
          )
        }
      />
      {totalAccounts > 1 && (
        <p className="text-xs text-indigo-200">
          Se crearán <strong>{totalAccounts}</strong> cuentas WABA, una por cada selección de scope.
        </p>
      )}
    </div>
  );
}

function SummaryRow({ label, value }) {
  return (
    <div className="flex items-start justify-between gap-4 rounded-lg bg-gray-800 p-3">
      <span className="text-xs font-medium uppercase tracking-wide text-gray-500">{label}</span>
      <div className="text-right text-sm text-gray-200">{value}</div>
    </div>
  );
}


// ────────────────────────────────────────────────────────────────────────────
// Main wizard
// ────────────────────────────────────────────────────────────────────────────
const INITIAL_FORM = {
  name: "",
  provider: "",
  credentials: {},
  scopeType: "global",
  scopeRefIds: [],
  modules: { notificaciones: true, logicas: true, campanas: true, agente_ia: true },
  // R2-A: fallback INDEPENDIENTE del scope. Default se ajusta al abrir según D2
  // (primer WABA de la empresa → ON; si ya existe otro → OFF).
  is_global_fallback: true,
};


export default function WabaWizard({ open, onClose, onSuccess }) {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(INITIAL_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [error, setError] = useState(null);
  const [devs, setDevs] = useState({ status: "idle", items: [] });
  const [products, setProducts] = useState({ status: "idle", items: [] });
  // R2-A / C2: flag para NO pisar la elección del usuario con el default del fallback.
  const fallbackTouched = useRef(false);
  const setFallback = (v) => {
    fallbackTouched.current = true;
    setForm((f) => ({ ...f, is_global_fallback: v }));
  };

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    // Fetch ambos endpoints siempre — la decisión de mostrar se basa en
    // los datos reales, no en business_type.
    setDevs({ status: "loading", items: [] });
    api
      .get("/developments")
      .then(({ data }) => {
        if (!cancelled) setDevs({ status: "ok", items: Array.isArray(data) ? data : [] });
      })
      .catch(() => {
        if (!cancelled) setDevs({ status: "error", items: [] });
      });
    setProducts({ status: "loading", items: [] });
    api
      .get("/products")
      .then(({ data }) => {
        if (!cancelled) setProducts({ status: "ok", items: Array.isArray(data) ? data : [] });
      })
      .catch(() => {
        if (!cancelled) setProducts({ status: "error", items: [] });
      });
    // R2-A: default del fallback (D2 — si la empresa NO tiene cuentas, arranca en ON;
    // si ya tiene, OFF). C2 — se aplica UNA vez y solo si el usuario no tocó el toggle.
    api
      .get("/waba-accounts")
      .then(({ data }) => {
        if (cancelled) return;
        const arr = Array.isArray(data) ? data : [];
        if (!fallbackTouched.current) {
          setForm((f) => ({ ...f, is_global_fallback: arr.length === 0 }));
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [open]);

  const reset = () => {
    setStep(1);
    setForm(INITIAL_FORM);
    setSubmitting(false);
    setProgress({ current: 0, total: 0 });
    setError(null);
    fallbackTouched.current = false;
  };

  const handleClose = () => {
    if (submitting) return;
    reset();
    onClose?.();
  };

  // ── Step 1 validation ────────────────────────────────────────────────
  const step1Valid = useMemo(() => {
    if (!form.name.trim() || !form.provider) return false;
    const provider = PROVIDERS.find((p) => p.key === form.provider);
    if (!provider) return false;
    return provider.fields
      .filter((f) => f.required)
      .every((f) => (form.credentials[f.key] || "").toString().trim());
  }, [form]);

  // ── Step 2 validation ────────────────────────────────────────────────
  const step2Valid = useMemo(() => {
    if (form.scopeType === "global") return true;
    return form.scopeRefIds.length > 0;
  }, [form.scopeType, form.scopeRefIds]);

  // ── Step 3 validation ────────────────────────────────────────────────
  const step3Valid = useMemo(
    () => Object.values(form.modules).some((v) => v === true),
    [form.modules]
  );

  // ── Build payload(s) ─────────────────────────────────────────────────
  const buildPayloads = () => {
    let phone_number = null;
    if (form.provider === "gupshup") phone_number = form.credentials.source_phone || null;
    else if (form.provider === "twilio") phone_number = form.credentials.phone || null;

    const isGlobal = form.scopeType === "global";
    const base = {
      name: form.name.trim(),
      provider: form.provider,
      credentials: { ...form.credentials },
      phone_number,
      modules: { ...form.modules },
      status: "active",
    };

    const fallback = !!form.is_global_fallback;

    if (isGlobal) {
      return [
        {
          ...base,
          is_global_fallback: fallback,
          scope: { type: "global", ref_id: null },
        },
      ];
    }

    // Con varias líneas (N refs), el fallback —si se pidió— va SOLO en la primera:
    // no pueden coexistir dos fallbacks (lo bloquea el backend). El resto va en false.
    return form.scopeRefIds.map((refId, idx) => ({
      ...base,
      is_global_fallback: idx === 0 ? fallback : false,
      scope: { type: form.scopeType, ref_id: refId },
    }));
  };

  // ── Submit ───────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const payloads = buildPayloads();
      setProgress({ current: 0, total: payloads.length });
      const created = [];
      for (let i = 0; i < payloads.length; i += 1) {
        const { data } = await api.post("/waba-accounts", payloads[i]);
        created.push(data);
        setProgress({ current: i + 1, total: payloads.length });
      }
      toast.success(
        created.length === 1
          ? "Cuenta WABA creada"
          : `${created.length} cuentas WABA creadas`
      );
      onSuccess?.(created);
      reset();
      onClose?.();
    } catch (err) {
      const raw = err?.response?.data?.detail;
      let message = "No fue posible crear la cuenta";
      if (typeof raw === "string" && raw) {
        message = raw;
      } else if (raw && typeof raw === "object") {
        // Conflict inter-tenant: el backend manda {detail, conflict_company_name, field}
        const field = raw.field || "número/App ID";
        const company = raw.conflict_company_name || "otra empresa";
        message = `Este ${field} ya está registrado en la cuenta: ${company}. Si es un error, contacta a soporte.`;
      }
      setError(message);
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const next = () => setStep((s) => Math.min(s + 1, 4));
  const back = () => setStep((s) => Math.max(s - 1, 1));

  const canAdvance =
    (step === 1 && step1Valid) ||
    (step === 2 && step2Valid) ||
    (step === 3 && step3Valid);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent
        className="max-w-2xl bg-gray-900 border border-gray-700 text-gray-100"
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
        data-testid="waba-wizard-dialog"
      >
        <DialogHeader>
          <DialogTitle className="text-white">Nueva cuenta WhatsApp</DialogTitle>
          <DialogDescription className="text-gray-400">
            Configura un nuevo número WABA en 4 pasos.
          </DialogDescription>
        </DialogHeader>

        <StepIndicator current={step} />

        <div className="max-h-[60vh] overflow-y-auto pr-1">
          {step === 1 && <Step1ProviderCreds form={form} setForm={setForm} />}
          {step === 2 && (
            <Step2Scope
              form={form}
              setForm={setForm}
              setFallback={setFallback}
              devs={devs}
              products={products}
            />
          )}
          {step === 3 && <Step3Modules form={form} setForm={setForm} />}
          {step === 4 && (
            <Step4Summary form={form} devs={devs.items} products={products.items} />
          )}
        </div>

        {error && (
          <div className="flex items-start gap-2 rounded-lg border border-red-500/40 bg-red-900/30 p-3 text-sm text-red-200">
            <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <DialogFooter className="flex !justify-between gap-2 sm:!justify-between">
          <Button
            variant="ghost"
            onClick={step === 1 ? handleClose : back}
            disabled={submitting}
            data-testid="waba-wizard-back"
          >
            {step === 1 ? "Cancelar" : (
              <span className="flex items-center gap-1">
                <ChevronLeft className="h-4 w-4" /> Atrás
              </span>
            )}
          </Button>

          {step < 4 ? (
            <Button
              onClick={next}
              disabled={!canAdvance}
              className="gap-1"
              data-testid="waba-wizard-next"
            >
              Siguiente <ChevronRight className="h-4 w-4" />
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={submitting}
              className="gap-2"
              data-testid="waba-wizard-submit"
            >
              {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
              {submitting
                ? progress.total > 1
                  ? `Creando ${progress.current}/${progress.total}…`
                  : "Creando…"
                : progress.total > 1 || form.scopeRefIds.length > 1
                ? `Crear ${form.scopeType === "global" ? 1 : form.scopeRefIds.length} cuentas`
                : "Crear cuenta"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
